import { type ComponentType, lazy } from 'react';

import type { RecoveryOptions } from '../survey-tool/utils/dynamic-import-recovery';
import { loadWithReload as loadWithRecovery } from '../survey-tool/utils/dynamic-import-recovery';
import { logWarn } from './log';

// The stale-chunk recovery itself (module validation, the one-shot reload guard, and the
// dynamic-import signature list) lives in the survey-tool submodule, which is compiled as
// first-party source here. This file supplies only the two pieces of policy that are
// specific to THIS app and deliberately not owned by the shared library: how a
// guard-exhausted failure is reported, and where the user is sent.
//
// It used to be a full copy of that logic. The duplication was a liability: the same
// correctness fix had to be applied twice in two repos, and the reload guard's
// sessionStorage key was a contract held together by a matching string literal on each
// side — a rename on either side would have caused a double reload. One implementation
// removes both problems.

function buildRefreshErrorUrl(): string {
  const title = encodeURIComponent('Update in progress');
  const desc = encodeURIComponent('Please refresh the page again in 5 minutes.');
  return `${window.location.origin}/error?title=${title}&desc=${desc}`;
}

// A persistent stale-chunk failure (reload guard already armed). Redirect to the
// /error page so the user sees an actionable "refresh in 5 minutes" message instead
// of a broken route. Loop guard: if the /error page's OWN chunk is what failed
// (we're already on /error), throw instead so the ErrorBoundary's inline,
// dependency-light fallback renders rather than bouncing back to /error forever.
async function redirectToErrorOrThrow<T extends ComponentType<any>>(thrown: unknown): Promise<{ default: T }> {
  if (window.location.pathname === '/error') {
    throw thrown;
  }
  window.location.href = buildRefreshErrorUrl();
  // Suspend forever while the browser navigates away, so React never renders a broken module.
  return await new Promise<{ default: T }>(() => {});
}

// Report via logWarn rather than the library's default console.error: a persistent stale
// chunk is an expected post-deploy condition, not an app bug, and logWarn ships it to
// telemetry at warn level directly instead of relying on console.error interception plus
// the effectiveLevel downgrade in log.ts.
const recoveryOptions: RecoveryOptions = {
  report: logWarn,
  onGuardExhausted: redirectToErrorOrThrow,
};

// Exported for unit testing.
export async function loadWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  return await loadWithRecovery(factory, recoveryOptions);
}

// Drop-in replacement for React.lazy for first-party route chunks.
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() => loadWithReload(factory));
}
